import { colord, extend } from "colord";
import labPlugin from "colord/plugins/lab";

const headerColors = {
  "$color__warm-grey--60": "#73706e",
  "$color__warm-grey--70": "#565452",
  "$color__warm-grey--80": "#393837",
  "$color__warm-grey--90": "#1d1c1b",
  "$color__grey--60": "#767676",
  "$color__grey--70": "#5b5b5b",
  "$color__grey--80": "#424242",
  "$color__grey--90": "#1a1a1a",
  "$color__grey--100": "#0a0a0a",
  "$color__blue--60": "#1a47b8",
  "$color__blue--70": "#0c3392",
  "$color__blue--80": "#042165",
  "$color__blue--90": "#011033",
  "$color__bronze--60": "#a36229",
  "$color__bronze--70": "#824816",
  "$color__bronze--80": "#5b2f09",
  "$color__bronze--90": "#2e1702",
  "$color__garden--60": "#009877",
  "$color__garden--70": "#007a60",
  "$color__garden--80": "#005543",
  "$color__garden--90": "#002b22",
  "$color__magenta--60": "#b32572",
  "$color__magenta--70": "#8e1356",
  "$color__magenta--80": "#630739",
  "$color__magenta--90": "#32021c",
  "$color__orange--60": "#bf4116",
  "$color__orange--70": "#982e0a",
  "$color__orange--80": "#691d03",
  "$color__orange--90": "#350e00",
  "$color__sky--60": "#1593ac",
  "$color__sky--70": "#097489",
  "$color__sky--80": "#03505f",
  "$color__sky--90": "#002830",
  "$color__sunset--60": "#b13317",
  "$color__sunset--70": "#8d220b",
  "$color__sunset--80": "#621504",
  "$color__sunset--90": "#310a01",
  "$color__yellow--60": "#bc9711",
  "$color__yellow--70": "#957707",
  "$color__yellow--80": "#675102",
  "$color__yellow--90": "#342900"
};

/**
 * gets the default color to display
 *
 * @returns {string}
 */
export function getDefaultColor() {
  return headerColors["$color__grey--60"];
}

/**
 * Gets the closest GettyUI color to a hex color
 *
 * @param {object} color - the hex color to lookup the closest GettyUI color to
 *
 * @returns {string} the hex for the GettyUI Color
 */
export function getClosestColor(color) {
  if (color == undefined) {
    return getDefaultColor();
  }

  extend([labPlugin]);
  const colors = Object.values(headerColors);
  let min = 0xffffff;
  let best, current, i;
  for (i = 0; i < colors.length; i++) {
    // convert RGB -> LAB color for a better distance calculation that takes into account hues
    current = colord(color).delta(colors[i]);
    if (current < min) {
      min = current;
      best = colors[i];
    }
  }
  return best;
}

/**
 * Tests whether a hex color is light or dark
 *
 * @param {string} hex
 *
 * @returns {boolean} - whether it's light or not
 */
export function isColorDark(hex) {
  let rgb = colord(hex).toRgb();
  const luma = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b; // per ITU-R BT.709

  // arbitrary #
  if (luma < 180) {
    return true;
  }
  return false;
}

/**
 * Gets the closest dark hex color in GettyUI in an array of colors
 *
 * @param {array} colors - an array of colors objects with r,g,b attributes
 *
 * @returns {string} the closest hex color
 */
export function getClosestDarkColor(colors) {
  // iterate through all of the colors in descending order, find a darker color in the image to use
  colors = colors.sort((a, b) => (a.percent < b.percent ? 1 : -1));
  let firstClosest = undefined;
  for (let i = 0; i < colors.length; i++) {
    let hexColor = colord([colors[i].r, colors[i].g, colors[i].b]).toHex();
    if (isColorDark(hexColor)) {
      // skip the 1st color
      let closest = getClosestColor(colors[i]);
      if (firstClosest == undefined) {
        firstClosest = closest;
        continue;
      }
      if (firstClosest != closest) {
        return closest;
      }
    }
  }
  return getDefaultColor();
}

/**
 * converts a RGB color to HSV
 * @param {integer} r - the red
 * @param {integer} g - the green
 * @param {integer} b - the blue
 *
 * @returns {object} - {h,s,v}
 */
export function rgbToHsv(r, g, b) {
  return colord({ r, g, b }).toHsv();
}
